//
import {
  defaultSettings,
  FontDetails,
  TemplateSettings,
} from 'modules/map/components/daily-dish/types';
import {
  LayoutId,
  WeekdayTitleStyle,
  WeekdayNumber,
} from 'modules/map/components/daily-dish/constants';

// Reducer Actions - Discriminated Union for Type Safety
export type SettingsAction =
  | { type: 'UPDATE_LOGO'; key: 'hvs' | 'client'; value: boolean }
  | { type: 'UPDATE_LAYOUT'; layoutId: LayoutId }
  | { type: 'UPDATE_LAYOUT_ALIGN'; align: 'left' | 'right' }
  | { type: 'UPDATE_BACKGROUND'; category: number; id: number }
  | { type: 'UPDATE_BACKGROUND_FILE'; file: string }
  | { type: 'UPDATE_LOCAL_BACKGROUND'; value: boolean }
  | { type: 'HYDRATE'; settings: TemplateSettings }
  | {
      type: 'UPDATE_FONT';
      section: keyof TemplateSettings['font'];
      details: Partial<FontDetails>;
    }
  | { type: 'UPDATE_WEEKDAY_COLOR'; weekday: WeekdayNumber; color: string }
  | {
      type: 'UPDATE_WEEKDAY_EMOJI';
      weekday: WeekdayNumber;
      emojiId: number;
    }
  | {
      type: 'UPDATE_MENU_STYLE';
      field: keyof Omit<TemplateSettings['menu'], 'weekday'>;
      value: any;
    }
  | {
      type: 'UPDATE_FAVORITE_SETTINGS_LIST';
      field: keyof TemplateSettings;
      value: TemplateSettings;
    }
  | { type: 'RESET' };

// Reducer Function
export function settingsReducer(state: TemplateSettings, action: SettingsAction): TemplateSettings {
  switch (action.type) {
    case 'UPDATE_LOGO':
      return {
        ...state,
        general: {
          ...state.general,
          showLogo: {
            ...state.general.showLogo,
            [action.key]: action.value,
          },
        },
      };

    case 'UPDATE_LAYOUT':
      return {
        ...state,
        general: {
          ...state.general,
          layoutId: action.layoutId,
        },
        menu: {
          ...state.menu,
          // Force weekdayTitleStyle to NORMAL when TwoRowsLayout is selected
          weekdayTitleStyle:
            action.layoutId === LayoutId.TWO_ROWS
              ? WeekdayTitleStyle.NORMAL
              : state.menu.weekdayTitleStyle,
        },
      };

    case 'UPDATE_LAYOUT_ALIGN':
      return {
        ...state,
        general: {
          ...state.general,
          layoutAlign: action.align,
        },
      };

    case 'UPDATE_BACKGROUND':
      return {
        ...state,
        general: {
          ...state.general,
          backgroundImage: {
            category: action.category,
            id: action.id,
          },
        },
      };

    case 'UPDATE_BACKGROUND_FILE':
      return {
        ...state,
        general: {
          ...state.general,
          backgroundFile: action.file,
          localBackground: false,
        },
      };

    case 'UPDATE_LOCAL_BACKGROUND':
      return {
        ...state,
        general: {
          ...state.general,
          localBackground: action.value,
        },
      };

    case 'HYDRATE':
      return {
        ...action.settings,
        favoritesList: state.favoritesList,
      };

    case 'UPDATE_FONT':
      return {
        ...state,
        font: {
          ...state.font,
          [action.section]: {
            ...state.font[action.section],
            ...action.details,
          },
        },
      };

    case 'UPDATE_WEEKDAY_COLOR':
      return {
        ...state,
        menu: {
          ...state.menu,
          weekday: {
            ...state.menu.weekday,
            [action.weekday]: {
              ...state.menu.weekday[action.weekday],
              borderColor: action.color,
            },
          },
        },
      };

    case 'UPDATE_WEEKDAY_EMOJI':
      return {
        ...state,
        menu: {
          ...state.menu,
          weekday: {
            ...state.menu.weekday,
            [action.weekday]: {
              ...state.menu.weekday[action.weekday],
              emojiId: action.emojiId,
            },
          },
        },
      };

    case 'UPDATE_MENU_STYLE':
      return {
        ...state,
        menu: {
          ...state.menu,
          [action.field]: action.value,
        },
      };

    case 'UPDATE_FAVORITE_SETTINGS_LIST':
      return {
        ...state,
        favoritesList: [...state.favoritesList, action.value],
      };

    case 'RESET':
      return defaultSettings;

    default: {
      // Exhaustive check - TypeScript will error if a case is missing
      action satisfies never;
      return state;
    }
  }
}
